import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import { Checkbox } from '../components/ui/checkbox.js';
import { SecurityProcessMenu } from '../components/SecurityProcessMenu.js';
import { useCustomer } from '../customer-context.js';
import { useContent } from '../content.js';
import { api } from '../api/client.js';
import infoIcon from '../assets/info-icon.svg';
import {
  InfoBox,
  QuestionRow,
  PassFailRadios,
  countDistinctAnswers,
  type CheckStatus,
  type SecurityQuestionsData,
} from './registration-common.js';

/**
 * SCRN 58 — Broker Medium Security (CCF-318 / #425).
 *
 * The Broker 24 MEDIUM-security verification screen. Per @dylan219's #425
 * ruling (2026-07-21) this is the page a broker medium-security process routes
 * to — NOT a direct link to the Reset Internet Password screen. The agent
 * completes the broker security questions here and, once security PASSES, the
 * screen takes the agent (via an explicit Continue — #846) to the Reset Internet Password screen
 * (SCRN-57) — a broker log-in is not linked to any accounts, so the customer
 * dashboard would be blank (CCF-318, Archbold 30/Jun/26).
 *
 * The story states this screen is "similar to Screen 21 (Broker 24
 * registration) except the last part directs to the password-reset screen
 * (SCRN-57) rather than the customer dashboard". It therefore reuses the
 * registration screens' shared building blocks (`InfoBox`, `QuestionRow`,
 * `PassFailRadios`) and the SAME RBAC-gated data route (`POST
 * /api/security/questions`, sourced from `/customer/identification` +
 * `/customer/info`) — no new gateway endpoint is introduced.
 *
 * Acceptance criteria (CCF-318):
 *   - Mandatory Pass/Fail on: broker full name, signatory address, eye colour,
 *     first school attended.
 *   - 4 additional questions (street grew up on / favourite music / favourite
 *     holiday destination / favourite sport), of which ONE must be answered;
 *     all on-file answers are shown in a display box, no specific order.
 *   - The overall Pass/Fail result is not shown until all questions are asked.
 *   - Any single Fail → overall Fail (branch/Broker-Representative referral).
 *   - If broker details are missing on file (a blank mandatory field in
 *     Unibanks) the screen shows a note and the agent cannot complete security.
 *   - On a successful PASS the screen shows the PASS flag + a "Continue to reset
 *     internet password" button (#846 — no auto-navigation); the click records
 *     the verification server-side and goes to `/reset-internet-password`.
 */

/**
 * The eye-colour / first-school hint mirrors the Broker24 *registration*
 * screen (SCRN-21): those two facts have no reliable on-file value to display
 * or diff against — the real gateway never populates `EyeColour`/`FirstSchool`
 * individually (mock-only convenience fields, #76/#334), only the unordered
 * free-text `AdditionalQuestionAnswers`. Per @dylan219's #349 ruling the agent
 * asks the customer directly and judges Pass/Fail against that shared free-text
 * list. (Broker Medium Security, SCRN-58, previously rendered these as
 * value-resolving rows that always showed "Not on file" with the Pass control
 * disabled on the real gateway — issue #458.)
 */
const SEE_ADDITIONAL_ANSWERS_HINT =
  'See answers below in additional questions.';

/** The mandatory identity items, each marked Pass/Fail (CCF-318). */
interface MandatoryItem {
  readonly key: string;
  readonly label: string;
  /**
   * Resolves the on-file value for display. Broker full name comes from the
   * identified customer context; the signatory address comes from the fetched
   * security-questions data. `undefined`/blank means the field is MISSING on
   * file (Unibanks blank) — the unhappy path (#2) below.
   */
  readonly value: (
    q: SecurityQuestionsData,
    name: string,
  ) => string | undefined;
  /**
   * An "external check" item (eye colour / first school attended, issue #458):
   * there is no automatic on-file value to display or diff against, so no value
   * is shown ("Not on file" is NEVER rendered) and the Pass/Fail control is
   * ALWAYS markable — the agent asks the customer and judges against the shared
   * free-text answers box below. Mirrors the Broker24 registration screen
   * (#349); these rows are EXCLUDED from the missing-on-file-data gate.
   */
  readonly externalCheck?: boolean;
  /** Grey sub-instruction shown beneath the label (external-check rows). */
  readonly hint?: string;
}

const MANDATORY_ITEMS: readonly MandatoryItem[] = [
  {
    key: 'name',
    label: "Broker's full name",
    value: (_q, name) => name || undefined,
  },
  {
    key: 'signatoryAddress',
    label: 'Signatory address',
    value: (q) => q.SignatoryAddress,
  },
  {
    key: 'eyeColour',
    label: 'Can you confirm your eye colour?',
    hint: SEE_ADDITIONAL_ANSWERS_HINT,
    externalCheck: true,
    value: () => undefined,
  },
  {
    key: 'firstSchool',
    label: 'Can you confirm the first school you attended?',
    hint: SEE_ADDITIONAL_ANSWERS_HINT,
    externalCheck: true,
    value: () => undefined,
  },
];

/**
 * The 4 additional questions (CCF-318): the agent ticks the ONE they are
 * asking and marks it Pass/Fail. The on-file answers are free text from
 * `customer/info` with no guaranteed order or 1:1 mapping to these questions
 * (an agent free-types into the source field elsewhere — issue #311's ruling,
 * mirrored here), so they are shown once as an unordered display box rather
 * than per-question.
 */
const ADDITIONAL_QUESTIONS: ReadonlyArray<{ key: string; label: string }> = [
  {
    key: 'street',
    label: 'Can you confirm the name of the street you grew up on?',
  },
  { key: 'music', label: 'Can you confirm your favourite type of music?' },
  {
    key: 'holiday',
    label: 'Can you confirm your favourite holiday destination?',
  },
  { key: 'sport', label: 'Can you confirm your favourite sport?' },
];

/** The route the agent is brought to once broker security PASSES (SCRN-57). */
const RESET_INTERNET_PASSWORD_ROUTE = '/reset-internet-password';

export function BrokerMediumSecurityScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const { customer } = useCustomer();
  // #637 (SCRN-56 / CCF-113) — the security-failed advisory copy is admin-
  // editable scripting text (content-catalogue keys); it falls back to the
  // compiled-in defaults. Rendered as PLAIN TEXT only (no HTML).
  const securityFailHeading = useContent('broker.medium.security.fail.heading');
  const securityFailAdvisory = useContent(
    'broker.medium.security.fail.advisory',
  );
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const brokerName = customer.name ?? '';

  const [questions, setQuestions] = useState<SecurityQuestionsData | null>(
    null,
  );
  const [loadError, setLoadError] = useState(false);
  // CCF-112 (#477): the context (customerNumber|tellerId) whose /security/questions
  // data is currently loaded. Stamped ON a successful load and cleared on a
  // context change, so the terminal Security Result event below can only fire
  // once THIS customer's own data has loaded — never against a stale
  // prior-customer outcome (the #243/#244/#422 stale-cross-customer guard).
  const [questionsContext, setQuestionsContext] = useState<string | null>(null);

  const [mandatoryStatus, setMandatoryStatus] = useState<
    Record<string, CheckStatus>
  >({});
  // Broker24 CCF-318: only ONE additional question needs to be answered. The
  // agent ticks which question they are asking; ticking a different question
  // clears any prior Pass/Fail mark so a stale mark can never keep counting.
  const [selectedAdditionalKey, setSelectedAdditionalKey] = useState<
    string | null
  >(null);
  const [additionalStatus, setAdditionalStatus] = useState<CheckStatus>(null);
  // #846 (operator ruling, 2026-10-08): a PASS no longer auto-navigates — the
  // agent presses "Continue to reset internet password", which records the
  // verification server-side and then navigates. `completing` guards against a
  // double-submit while that is in flight; the POSTs are only ever driven by an
  // explicit click (Continue or Retry), so a persistent 403/500 can never cause
  // an automatic re-post loop (#425).
  const [completing, setCompleting] = useState(false);
  // Set if the server-side completion call fails: we must NOT navigate to the
  // Reset Internet Password screen (SCRN-57) if the broker's `medium`
  // verification was not recorded server-side — that route would 403. Fail
  // closed to a STABLE error state and surface the error inline; the agent must
  // press Retry to re-attempt.
  const [completionError, setCompletionError] = useState<{
    code?: string;
    message: string;
  } | null>(null);

  // Load the identity-question data once a customer + Teller ID are in context.
  useEffect(() => {
    setMandatoryStatus({});
    setSelectedAdditionalKey(null);
    setAdditionalStatus(null);
    setQuestionsContext(null);
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      setLoadError(true);
      setQuestions(null);
      return;
    }
    let cancelled = false;
    setLoadError(false);
    setQuestions(null);
    void (async () => {
      try {
        // Begin a server-side broker-medium run for this identified broker
        // (issue #425): the broker Pass/Fail checks below run only in the UI, so
        // this run is the server-side attestation that the broker flow was
        // opened for THIS customer. `/security/broker-medium/complete` requires
        // it, so a direct POST to `/complete` that skipped this screen 403s. A
        // begin failure is non-fatal to loading (a persistent one surfaces later
        // as a fail-closed completion error), so it does not block the data.
        await api.POST('/security/broker-medium/begin', {
          params: { header: { 'X-Teller-Id': tellerId } },
        });
        if (cancelled) return;
        const { data, response } = await api.POST('/security/questions', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (cancelled) return;
        if (!response.ok || !data) {
          setLoadError(true);
          return;
        }
        setQuestions(data as SecurityQuestionsData);
        // Stamp the context this data belongs to (CCF-112, #477) — see the
        // terminal Security Result event guard below.
        setQuestionsContext(`${customerNumber}|${tellerId}`);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId]);

  const noContext = !customerNumber;
  // Fail closed: work is only possible once a customer + valid Teller ID are in
  // context and the security-question data has loaded without error.
  const dataReady =
    !!customerNumber && /^\d{4}$/.test(tellerId) && !!questions && !loadError;

  // Resolve each mandatory item's on-file value. An item with no value is
  // MISSING on file (Unibanks blank) — CCF-318 unhappy path #2.
  const mandatoryValue = (m: MandatoryItem): string | undefined => {
    if (!questions) return undefined;
    const v = m.value(questions, brokerName);
    return v && v.trim().length > 0 ? v : undefined;
  };
  // Any mandatory identity field blank on file → the agent cannot complete
  // security (missing-data unhappy path). Only evaluated once data has loaded.
  // External-check rows (eye colour / first school, #458) NEVER carry an
  // on-file value by design, so they must be excluded — otherwise the screen
  // would always report the broker's details as missing on the real gateway.
  const missingMandatoryData =
    dataReady &&
    MANDATORY_ITEMS.filter((m) => !m.externalCheck).some(
      (m) => !mandatoryValue(m),
    );

  // The shared free-text on-file answers for the 4 additional questions. The
  // agent needs AT LEAST ONE distinct on-file answer to be able to mark the
  // selected question (mirrors the registration screens' `requiredAdditional`
  // gate — a blank/duplicate list must not enable marking Pass).
  const additionalAnswers = questions?.AdditionalQuestionAnswers ?? [];
  const additionalAnswerCount = countDistinctAnswers(additionalAnswers);
  const additionalDataReady = dataReady && additionalAnswerCount >= 1;

  // CCF-318 Unhappy Path #2 — insufficient data on file (#772, @dylan219).
  // When the broker has NO security questions on file (the free-text
  // `AdditionalQuestionAnswers` list is empty) there is nothing to verify the
  // additional questions — OR the eye-colour / first-school external-check rows,
  // which the agent judges against that SAME shared list (#458) — against, so
  // security cannot be completed. Previously the screen only surfaced a note
  // when a value-resolving mandatory field (name / signatory address) was blank
  // (`missingMandatoryData`), leaving the empty-questions case just sitting blank
  // (the reported symptom). Treated as a terminal insufficient-data state
  // alongside the missing-mandatory-field case.
  const noQuestionsOnFile = dataReady && additionalAnswerCount === 0;

  const setMandatory = (key: string, v: 'pass' | 'fail'): void =>
    setMandatoryStatus((s) => ({ ...s, [key]: v }));

  const selectAdditional = (key: string, checked: boolean): void => {
    setSelectedAdditionalKey((prev) =>
      checked ? key : prev === key ? null : prev,
    );
    setAdditionalStatus(null);
  };

  // Every mandatory item is marked Pass. Value-resolving rows additionally
  // require an on-file value; external-check rows (eye colour / first school,
  // #458) pass on the agent's mark alone (they never carry a value).
  const mandatoryAllPass =
    !missingMandatoryData &&
    MANDATORY_ITEMS.every((m) =>
      m.externalCheck
        ? mandatoryStatus[m.key] === 'pass'
        : !!mandatoryValue(m) && mandatoryStatus[m.key] === 'pass',
    );
  // The one selected additional question has an on-file answer to verify
  // against AND is marked Pass.
  const additionalPass =
    selectedAdditionalKey !== null &&
    additionalDataReady &&
    additionalStatus === 'pass';

  // "All questions asked": every mandatory item has an explicit Pass/Fail AND
  // the agent has selected + marked one additional question. The overall
  // result is not shown until this is true (CCF-318).
  const allQuestionsAsked =
    dataReady &&
    MANDATORY_ITEMS.every((m) => mandatoryStatus[m.key] != null) &&
    selectedAdditionalKey !== null &&
    additionalStatus != null;

  // Insufficient data on file (CCF-318 unhappy path #2): either a value-resolving
  // mandatory field is blank, OR there are no security questions on file at all
  // (#772). Both terminally prevent completing security.
  const insufficientData = missingMandatoryData || noQuestionsOnFile;

  // Any single Fail → overall Fail (CCF-318). An insufficient-data record is also
  // a terminal failure of the flow (unhappy path #2).
  const anyFail =
    MANDATORY_ITEMS.some((m) => mandatoryStatus[m.key] === 'fail') ||
    additionalStatus === 'fail' ||
    insufficientData;

  // Overall PASS: all questions asked, everything passed, nothing failed.
  const securityPass =
    allQuestionsAsked && mandatoryAllPass && additionalPass && !anyFail;
  // The FAIL result is shown once all questions have been asked and at least
  // one failed (or, for the insufficient-data path, as soon as the gap is known).
  const securityFail = (allQuestionsAsked && anyFail) || insufficientData;

  // CCF-112 (#477): record the Broker Medium screen's TERMINAL outcome (Passed /
  // Failed) as the SAME `Medium Security Result` Non-API business event the
  // shared Medium/High + ECL security screens already fire. The broker Pass/Fail
  // decision is a pure client-side screen state (the `/attest` route only stores
  // it on the run for the elevation gate — it does not write Call History), so —
  // mirroring the ECL Medium variant — the event is fired best-effort here,
  // exactly once per terminal outcome, via the existing `/calls/security-event`
  // route. A once-guard (keyed to customer + Teller context) prevents a re-fire
  // on re-render and resets when the context changes, so a fresh customer is
  // recorded again. A Call-History write failure must NEVER break the flow.
  //
  // Stale-outcome-crosses-customers guard (#243/#244/#422): on a context swap the
  // load effect (which clears the marks + `questionsContext`) and this effect can
  // run in the same commit while `customerNumber` is already the NEW CIF but the
  // Pass/Fail marks still derive from the PREVIOUS customer. `questionsForContext`
  // is `false` on the first render of a new context (its stamp still holds the
  // OLD key) and stays false until THIS customer's own /security/questions load
  // re-stamps it, so a terminal outcome can never be attributed to the wrong CIF.
  const lastContextKey = `${customerNumber}|${tellerId}`;
  const questionsForContext = questionsContext === lastContextKey;
  const securityEventFiredRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (securityEventFiredRef.current === lastContextKey) return;
    if (!customerNumber || !questionsForContext) return;
    const outcome: 'Passed' | 'Failed' | null = securityPass
      ? 'Passed'
      : securityFail
        ? 'Failed'
        : null;
    if (!outcome) return;
    securityEventFiredRef.current = lastContextKey;
    void api
      .POST('/calls/security-event', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, securityLevel: 'medium', outcome },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
    // `tellerId` is part of `lastContextKey`; excluded to keep the effect keyed
    // to the terminal-outcome transition for the current customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityPass, securityFail, customerNumber, questionsForContext]);

  // On "Continue to reset internet password" (shown once security PASSES —
  // #846, no auto-navigation), RECORD the broker's `medium` verification
  // server-side FIRST, then navigate to the Reset Internet Password screen
  // (SCRN-57) — NOT the dashboard (CCF-318). Recording is mandatory: SCRN-57's
  // `/security/internet-password` route is `requireVerified` (medium)-gated, so
  // without raising the session level the reset call would 403.
  //
  // FAIL CLOSED to a STABLE state (issue #425, ai-review MEDIUM): each click
  // posts EXACTLY ONCE. On error it surfaces the gateway/BFF Code + Message
  // inline and does NOT navigate; a re-attempt happens ONLY when the agent
  // presses Retry.
  const completeAndContinue = async (): Promise<void> => {
    if (!securityPass || completing) return;
    setCompleting(true);
    setCompletionError(null);
    try {
      // FIRST record the agent-attested PASS outcome server-side, bound to the
      // active run (#425): the broker Pass/Fail decision is made in this UI and
      // never reaches the server, so `/security/broker-medium/complete` will
      // only elevate to `medium` once this attested `pass` is on the run. This
      // is what makes a bare `begin`→`complete` bypass fail closed.
      const attest = await api.POST('/security/broker-medium/attest', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, outcome: 'pass' },
      });
      if (!attest.response.ok || !attest.data) {
        const err = attest.error as
          | { error?: string; required?: string }
          | undefined;
        setCompletionError({
          code: err?.error ?? String(attest.response.status),
          message:
            'Broker security passed, but we could not record the verification.',
        });
        return;
      }
      const { data, error, response } = await api.POST(
        '/security/broker-medium/complete',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        },
      );
      if (!response.ok || !data) {
        const err = error as { error?: string; required?: string } | undefined;
        setCompletionError({
          code: err?.error ?? String(response.status),
          message:
            'Broker security passed, but we could not record the verification.',
        });
        return;
      }
      toast.success(
        'Broker security passed. Continuing to reset internet password.',
      );
      navigate(RESET_INTERNET_PASSWORD_ROUTE);
    } catch {
      setCompletionError({
        message:
          'Broker security passed, but we could not record the verification.',
      });
    } finally {
      setCompleting(false);
    }
  };

  // On an overall FAIL (any Fail marked, or missing-on-file data), record the
  // fail-closed outcome server-side against the active run (#425) so the verdict
  // reaches + is audited by the server. This never elevates and never navigates
  // — `/complete` will still 403 (`run.outcome !== 'pass'`). Posts EXACTLY ONCE
  // (the `failAttested` latch), and a network failure is non-fatal (the fail is
  // terminal in the UI regardless).
  const [failAttested, setFailAttested] = useState(false);
  useEffect(() => {
    if (!securityFail || failAttested) return;
    setFailAttested(true);
    void api
      .POST('/security/broker-medium/attest', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, outcome: 'fail' },
      })
      .catch(() => {
        /* Non-fatal: the FAIL is terminal in the UI regardless. */
      });
  }, [securityFail, failAttested, tellerId, customerNumber]);

  // Explicit user retry after a failed completion — re-runs the SAME
  // attest → complete → navigate sequence exactly once.
  const retryCompletion = (): void => {
    void completeAndContinue();
  };

  return (
    <main
      className="max-w-[900px] pl-12 pr-12 py-12"
      aria-labelledby="broker-medium-heading"
    >
      <div className="mb-6 flex items-start justify-between gap-8 flex-wrap">
        <h1
          id="broker-medium-heading"
          className="text-[24px] font-['Arial',sans-serif] font-bold text-[#FF703E] m-0"
        >
          Broker Security
        </h1>
        {/* Security cross-navigation (issue #474, per @dylan219 #101): every
            medium-security page carries the shared options dropdown so an agent
            can swivel to the other medium pages (and their own relevant high
            page) plus the consistent servicing entries. The Broker medium page
            was missing it; add it here, consistent with the EDS/ECL medium
            siblings. `SecurityProcessMenu` self-excludes SCRN-58 from its own
            list (#474). Pure navigation — every entry targets an existing
            RBAC-gated route, re-checked on arrival (deny-by-default). */}
        <SecurityProcessMenu currentId="SCRN-58" />
      </div>

      {noContext && (
        <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3 mb-6">
          <img
            src={infoIcon}
            alt=""
            aria-hidden="true"
            className="w-5 h-5 mt-1 shrink-0"
          />
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
            No customer is in context. Identify the broker on the ID&amp;V
            screen first.
          </p>
        </div>
      )}

      {loadError && !noContext && (
        <div
          role="alert"
          className="bg-white border-2 border-[#dc2626] p-6 shadow-sm mb-6"
        >
          <p className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0">
            Unable to load the broker&apos;s security details. Retry, or
            escalate if the problem persists.
          </p>
        </div>
      )}

      {/* Insufficient-data unhappy path (CCF-318 #2): either a mandatory field is
          blank on file in Unibanks, or there are no security questions on file at
          all (#772) — in both cases the agent cannot complete security. Advise
          the customer to contact their Broker Representative / IMC. */}
      {insufficientData && (
        <div
          role="alert"
          data-testid="missing-data-note"
          className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3 mb-6"
        >
          <img
            src={infoIcon}
            alt=""
            aria-hidden="true"
            className="w-5 h-5 mt-1 shrink-0"
          />
          <div>
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
              There is not enough information on file to complete security.
            </p>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              Advise the broker to contact their Broker Representative / IMC.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Mandatory identity items (CCF-318). */}
        {/* #776: align the Broker Security section headers with the Broker 24
            registration screen — the header is a dark badge bar sitting ABOVE
            the white info box, not a plain dark-text heading inside it. */}
        <div className="space-y-3">
          <div className="bg-[#1D252C] text-white px-6 py-3 inline-block">
            <h2 className="text-[16px] font-['Arial',sans-serif] font-bold m-0">
              Mandatory selection
            </h2>
          </div>
          <InfoBox>
            <div className="space-y-6">
              {MANDATORY_ITEMS.map((m, idx) => {
                const value = mandatoryValue(m);
                const hasValue = !!value;
                return (
                  <React.Fragment key={m.key}>
                    {idx > 0 && <div className="border-t-2 border-[#dad7d3]" />}
                    <QuestionRow
                      label={m.label}
                      // External-check rows (eye colour / first school, #458)
                      // show the hint and NO on-file value — never "Not on file"
                      // — and stay markable once data has loaded; the agent
                      // judges them against the free-text answers box below.
                      hint={m.hint}
                      value={
                        m.externalCheck
                          ? undefined
                          : hasValue
                            ? value
                            : 'Not on file'
                      }
                      name={`mandatory-${m.key}`}
                      status={mandatoryStatus[m.key] ?? null}
                      onChange={(v) => setMandatory(m.key, v)}
                      disabled={!dataReady || (!m.externalCheck && !hasValue)}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </InfoBox>
        </div>

        {/* Additional security questions — confirm any ONE of four (CCF-318).
            #776: the instruction line itself is the dark badge bar above the
            white info box (matching Broker 24 registration), replacing the
            separate "Additional security questions" heading. */}
        <div className="space-y-3">
          <div className="bg-[#1D252C] px-6 py-3 inline-block">
            <h2 className="text-[16px] font-['Arial',sans-serif] m-0">
              <span className="font-bold text-white">
                Tick the box beside the ONE question you are asking the broker,
                then mark Pass or Fail against the answers on file shown below.
              </span>
            </h2>
          </div>
          <InfoBox>
            <div className="mb-4">
              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mb-1">
                Answers on file (free text — order not guaranteed):
              </p>
              {additionalAnswers.length ? (
                <ul className="list-disc pl-5 space-y-1">
                  {additionalAnswers.map((a, i) => (
                    <li
                      key={i}
                      className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                    >
                      {a}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                  Not on file
                </p>
              )}
            </div>
            <div className="space-y-6">
              {ADDITIONAL_QUESTIONS.map((q, idx) => {
                const isSelected = selectedAdditionalKey === q.key;
                return (
                  <React.Fragment key={q.key}>
                    {idx > 0 && <div className="border-t-2 border-[#dad7d3]" />}
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`additional-select-${q.key}`}
                        checked={isSelected}
                        onCheckedChange={(v) =>
                          selectAdditional(q.key, v === true)
                        }
                        disabled={!dataReady}
                      />
                      <label
                        htmlFor={`additional-select-${q.key}`}
                        className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                      >
                        {q.label}
                      </label>
                    </div>
                    {isSelected && (
                      <div className="ml-9 mt-3">
                        <PassFailRadios
                          name={`additional-${q.key}`}
                          value={additionalStatus}
                          onChange={(v) => setAdditionalStatus(v)}
                          disabled={!additionalDataReady}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* #846 (CCRS-3940): the overall PASS / FAIL result flag, at the
                bottom of the additional-questions box — the same placement as
                Broker 24 Registration (SCRN-21, Figma "Pass/Fail Flags for
                Box 2"; CCF-318: this screen is "similar to Screen 21"). Driven
                by `securityPass` / `securityFail`, so it only shows once all
                questions have been asked (CCF-318, #263) and recomputes live
                on a Fail→Pass correction (#840). */}
            {(securityPass || securityFail) && (
              <div className="flex items-center gap-4 flex-wrap mt-4">
                {securityPass && (
                  <span
                    className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
                    aria-label="Verification passed"
                  >
                    PASS
                  </span>
                )}
                {securityFail && (
                  <span
                    className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                    aria-label="Verification failed"
                  >
                    FAIL
                  </span>
                )}
              </div>
            )}
          </InfoBox>
        </div>

        {/* Fail-closed on the server-side completion call (CCF-318 / #425): the
            broker security PASSED, but recording `medium` verification
            server-side failed — so we did NOT navigate to Reset Internet
            Password (that route is medium-gated and would 403). Surface the
            Code + Message inline (recoverable) and require an EXPLICIT Retry —
            the completion is never auto-re-posted (no retry loop). */}
        {securityPass && completionError && (
          <div
            role="alert"
            className="bg-white border-2 border-[#dc2626] p-6 shadow-sm"
          >
            <p className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0 mb-3">
              {completionError.message} Please retry, or escalate if the problem
              persists.
              {completionError.code ? (
                <span className="block text-[14px] mt-1">
                  Code: {completionError.code}
                </span>
              ) : null}
            </p>
            <Button
              type="button"
              onClick={retryCompletion}
              className="bg-[#FF703E] text-white rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Retry
            </Button>
          </div>
        )}

        {securityFail && (
          <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
            <img
              src={infoIcon}
              alt=""
              aria-hidden="true"
              className="w-5 h-5 mt-1 shrink-0"
            />
            <div>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2 whitespace-pre-line">
                {securityFailHeading}
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 whitespace-pre-line">
                {securityFailAdvisory}
              </p>
            </div>
          </div>
        )}

        {/* #846 (operator ruling, 2026-10-08): a PASS does NOT auto-navigate.
            The agent continues explicitly — same style/placement convention as
            "Continue to dashboard" on the other security screens. The click
            records the verification server-side (attest → complete) and then
            navigates to SCRN-57; on a recording failure the Retry alert above
            replaces this button. FAIL shows no button. */}
        {securityPass && !completionError && (
          <div className="flex">
            <Button
              type="button"
              onClick={() => void completeAndContinue()}
              disabled={completing}
              aria-label="Continue to reset internet password"
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Continue to reset internet password
            </Button>
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center gap-6 flex-wrap">
        <Button
          type="button"
          onClick={() => navigate('/idv')}
          aria-label="Back to ID&V"
          className="bg-white text-[#1d252c] border-2 border-[#1d252c] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
        >
          Back to ID&amp;V
        </Button>
      </div>
    </main>
  );
}
